import React from 'react';

import { createBrowserRouter, RouterProvider } from 'react-router-dom';
import Dashboard from '../modules/dashboard';
import Login from '../modules/login';
import customers from '../modules/customers';
import PatchCenterLayout from '../layouts/PatchCenterLayout';
import patchcenter from './../modules/patchcenter/patchcenter';
import PatchAction from '../modules/patchcenter/patchAction';
import AvailablePatch from '../modules/patchcenter/AvailablePatch';
import PredictiveAnalysis from '../modules/patchcenter/PredictiveAnalysis';
import selectDeviceRoute from '../components/patchCenter/selectDeviceRoute';
import DeviceManagement from '../modules/deviceManagement';
import GroupPolicy from '../modules/groupPolicy';
import CreatePolicy from '../modules/groupPolicy/CreatePolicy';
import ThirdPartyPolicy from '../modules/groupPolicy/ThirdPartyPolicy';
import OsUpdate from '../modules/groupPolicy/OsUpdate';
import SoftwareDeployment from '../modules/softwareDeployment';
import PatchDeployment from '../modules/softwareDeployment/PatchDeployment';
import PreviewApprove from '../modules/patchcenter/PreviewApprove';
import Approve from '../modules/patchcenter/Approve';
import ApproveFromEmail from '../modules/patchcenter/ApproveFromEmail';
import OSUpdatePatch from '../modules/patchcenter/OSUpdatePatch';
import CreateGroup from '../modules/groupPolicy/CreateGroup';
import MaintananceGroup from '../modules/groupPolicy/MaintananceGroup';
import CreateCustomer from '../modules/customers/CreateCustomer';
import ActivityPatch from '../components/patchCenter/ActivityPatch';
import Availability from '../components/patchCenter/Availability';
import Assets from '../modules/softwareDeployment/assets';
import DeviceInfoBase from '../modules/deviceManagement/deviceManagementInfo';
import SoftwareInfo from '../modules/softwareDeployment/SoftwareInfo';
import Accounts from '../components/Accounts/Accounts';
import CreateAccount from '../components/Accounts/CreateAccount';
import PolicyAutomationLayout from '../layouts/PolicyAutomationLayout';
import GroupBase from '../modules/groupPolicy/GroupBase';
import Reboot from '../modules/groupPolicy/Reboot';
import ScriptAutomation from '../modules/ScriptAutomation';
import CreateNewScript from '../modules/ScriptAutomation/CreateNewScript';
import PatchReport from '../components/patchCenter/Report';
import DeviceInsights from '../components/patchCenter/DeviceInsights';
import GuideAndSupport from '../modules/Guide&Support';
import GuideDefatom from '../modules/Guide&Support/GuideDefatom';
import SupportDefatom from '../modules/Guide&Support/SupportDefatom';
import UploadScriptForm from '../modules/ScriptAutomation/UploadScriptForm';
import ProtectedRoute from './ProtectedRoute';
import TenantScopeGuard from './TenantScopeGuard';
import DeviceNeedingPatch from '../modules/patchcenter/DeviceNeedingPatch';
import DeviceNeedingPatchPage from '../modules/patchcenter/DeviceNeedingPatchPage';
import index from '../modules/PackageManager';
import Settings from '../modules/settings';
import VulnerabilityCenter from '../modules/VulnerabilityCenter';
import Report from '../modules/Reports/Report';
import { isDevelopmentEnvironment } from '../common/Utils/environment';
import path from 'path';
import ErrorPage from '../components/ErrorPage';
import ActivityLog from '../modules/ActivityLog';
import { ACTIVITY_LOG_ALLOWED_ROLES } from '../common/Utils/permissions';

const showMarketplace = isDevelopmentEnvironment();

const router = createBrowserRouter([
  {
    // Pathless root: catches render errors from any page and unknown URLs (404).
    errorElement: <ErrorPage />,
    children: [
      {
        // Own-tenant scope for Globaladmin/reseller: pages that need a customer tenant show "Select a tenant".
        element: <TenantScopeGuard />,
        children: [
          {
            path: '/',
            Component: Dashboard,
          },
          {
            path: '/Dashboard/:groupID',
            Component: Dashboard,
          },
          {
            path: '/login',
            Component: Login,
          },
          {
            path: '/customers',
            Component: customers,
          },
          {
            element: <ProtectedRoute allowedRoles={['globaladmin', 'reseller']} />, // protect with role check
            children: [
              {
                path: '/customers/CreateCustomer',
                Component: CreateCustomer,
              },
            ],
          },
          {
            path: '/patchCenter',
            Component: PatchCenterLayout,
            children: [
              { path: '', Component: patchcenter },
              { path: 'ActivityPatch', Component: ActivityPatch },
              { path: '/patchCenter/AvailabilityPatch', Component: Availability },
              { path: 'PredictiveAnalysis', Component: PredictiveAnalysis },
              { path: 'ApproveFromEmail/:planId', Component: ApproveFromEmail },
              { path: 'RemediationReport/:id', Component: PatchReport },
              { path: ':remediationPlanId/DeviceInsights/:agentId', Component: DeviceInsights },
              { path: 'DeviceNeedingPatch/:id', Component: DeviceNeedingPatchPage },
              {
                // Remediation-plan creation wizard: view-only roles (e.g. tenantuser) are redirected away.
                element: <ProtectedRoute requireEditAccess="remediationPlan" />,
                children: [
                  { path: 'deviceSelect', Component: selectDeviceRoute },
                  { path: 'Action', Component: PatchAction },
                  { path: 'AvailablePatch', Component: AvailablePatch },
                  { path: 'OSUpdatePatch', Component: OSUpdatePatch },
                  { path: 'Preview', Component: PreviewApprove },
                  { path: 'Preview&Approve', Component: Approve },
                ],
              },
            ],
          },
          {
            path: '/deviceManagement/:deviceHealth?',
            Component: DeviceManagement,
          },
          {
            path: '/deviceManagement/info/:id',
            Component: DeviceInfoBase,
          },
          {
            path: '/vulnerabilityCenter',
            Component: VulnerabilityCenter,
          },
          {
            path: '/Group',
            Component: GroupBase,
          },
          {
            path: '/groupPolicy',
            Component: PolicyAutomationLayout,
            children: [
              { path: '', Component: GroupPolicy },
              // Dual-purpose: create/edit (edit-access roles) or read-only step-through (view-access roles) - gated in-component.
              { path: 'CreatePolicy/:id?', Component: CreatePolicy },
              //{ path: 'EditPolicy/:id', Component: CreatePolicy },
              { path: 'CreateGroup/:id?', Component: CreateGroup },
              { path: 'MaintananceGroup', Component: MaintananceGroup },
              { path: 'Reboot', Component: Reboot },
              { path: 'ThirdParty', Component: ThirdPartyPolicy },
              { path: 'OS-Update', Component: OsUpdate },
            ],
          },

          {
            path: '/SoftwareDeployment',
            Component: SoftwareDeployment,
          },
          {
            path: '/SoftwareAssets',
            Component: Assets,
          },
          {
            path: '/SoftwareAssets/:type/:id',
            Component: SoftwareInfo,
          },
          {
            element: <ProtectedRoute requireEditAccess="softwareDeployment" />,
            children: [
              {
                path: '/SoftwareDeployment/PatchDeployment',
                Component: PatchDeployment,
              },
            ],
          },
          {
            path: '/Accounts',
            Component: Accounts,
          },
          {
            element: <ProtectedRoute requireEditAccess="accessControl" />,
            children: [
              {
                path: '/Accounts/CreateAccount',
                Component: CreateAccount,
              },
            ],
          },
          {
            path: '/ScriptAutomation',
            Component: ScriptAutomation,
          },
          {
            element: <ProtectedRoute requireEditAccess="scriptCenter" />,
            children: [
              {
                path: '/ScriptAutomation/CreateNewScript',
                Component: CreateNewScript,
              },
              {
                path: '/ScriptAutomation/EditScript/:id',
                Component: CreateNewScript,
              },
            ],
          },
          {
            path: '/GuideAndSupport',
            Component: GuideAndSupport,
          },
          {
            path: '/GuideAndSupport/Guide',
            Component: GuideDefatom,
          },
          {
            path: '/GuideAndSupport/Support',
            Component: SupportDefatom,
          },
          {
            path: '/Report',
            Component: Report,
            children: [
              {
                element: <ProtectedRoute requireEditAccess="report" />,
                children: [
                  {
                    path: '/Report/Generate',
                    Component: Report,
                  },
                ],
              },
              {
                path: '/Report/Download',
                Component: Report,
              },
            ],
          },
          {
            // Same roles as the AuditAPI's tenant endpoints; the global/cross-tenant views are gated in-page.
            element: <ProtectedRoute allowedRoles={ACTIVITY_LOG_ALLOWED_ROLES} />,
            children: [
              {
                path: '/activity-log',
                Component: ActivityLog,
              },
            ],
          },
          ...(showMarketplace
            ? [
                {
                  path: '/marketplace/*',
                  Component: require('../modules/Marketplace').default,
                },
              ]
            : []),
          {
            element: <ProtectedRoute allowedRoles={['globaladmin']} />,
            children: [
              {
                path: '/packagemanager',
                Component: index,
              },
              {
                path: '/packagemanager/addapps',
                Component: index,
              },
              {
                path: '/packagemanager/cveupdates',
                Component: index,
              },
            ],
          },
          {
            element: <ProtectedRoute allowedRoles={['globaladmin']} />,
            children: [
              {
                path: '/settings',
                Component: Settings,
              },
            ],
          },
          {
            path: '/settings/theme',
            Component: Settings,
          },
        ],
      },
    ],
  },
]);
const Router = () => {
  return <RouterProvider router={router} />;
};
export default Router;
